"use client";

import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Plus,
  Loader2,
  Trash2,
  Play,
  CheckCircle2,
  ArrowLeft,
  LocateFixed,
  Sparkles,
  Route,
  X,
  Phone,
  Mail,
  StickyNote,
  Send,
  User,
  RotateCw,
  UserPlus,
  Search,
  DoorOpen,
  Home,
  SkipForward,
  CalendarPlus,
  Trophy,
  ThumbsDown,
  Smartphone,
  Copy,
  MapPin,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import InsuraBotIcon from "@/app/(authenticated)/ai-calling/_modules/components/InsuraBotIcon";
import { LeadDetailsPanel } from "@/app/(authenticated)/leads/_modules/components/LeadDetailsPanel";
import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import NoSaleReasonModal from "@/app/(authenticated)/leads/_modules/components/NoSaleReasonModal";
import getStatusColor from "@/app/(authenticated)/leads/_modules/constants/get-status-color";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useInfiniteQuery } from "@tanstack/react-query";
import { showToast } from "@/app/_module/utils/show-toast";

import {
  useGetRoutes,
  useOptimizeRoute,
  useUpdateRoute,
  useDeleteRoute,
} from "./_modules/hooks/queries";
import { RouteMap } from "./_modules/components/RouteMap";
import { RouteLeadList } from "./_modules/components/RouteLeadList";
import { useRouteProgress } from "./_modules/contexts/RouteProgressContext";
import type { RoutePlan, Lead } from "./_modules/types/types";
import { isDemoMode } from "@/app/_module/utils/demoMode";

function formatDistance(meters: number): string {
  const miles = meters / 1609.34;
  return `${miles.toFixed(1)} mi`;
}

function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

// Active-route protection flag — time-bounded so an abandoned in-progress
// route can't keep an agent permanently logged in. Stamps the current time
// when the route is "alive" (page open, GPS ping, disposition action).
// useAutoStatusManager treats values older than 30 min as expired.
const refreshActiveRouteFlag = () => {
  if (typeof window === "undefined") return;
  try {
    const ts = String(Date.now());
    sessionStorage.setItem("insuracentral_active_route", ts);
    localStorage.setItem("insuracentral_active_route", ts);
  } catch {
    // storage may be unavailable in some contexts — ignore.
  }
};
const clearActiveRouteFlag = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem("insuracentral_active_route");
    localStorage.removeItem("insuracentral_active_route");
  } catch {
    // ignore
  }
};

// ─── QUICK DISPOSITION DOCK BUTTON ────────────────────────────────────────────
// Big tap-target buttons used by the in-progress route map dock. Designed
// for one-handed phone use on a porch — no menus, no submenus.
function DispositionButton({
  icon,
  label,
  sublabel,
  color,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  color: "emerald" | "amber" | "slate" | "blue" | "violet" | "rose";
  onClick: () => void;
}) {
  // Tailwind needs the full class names visible to the JIT compiler — can't
  // build them dynamically from the color string. Map explicitly.
  const palette: Record<typeof color, string> = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 active:bg-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 active:bg-amber-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 active:bg-slate-200",
    blue:    "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 active:bg-blue-200",
    violet:  "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 active:bg-violet-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 active:bg-rose-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-0.5 rounded-lg border px-2 py-2 transition-colors ${palette[color]}`}
    >
      {icon}
      <span className="text-[11px] font-semibold leading-tight">{label}</span>
      {sublabel && <span className="text-[9px] opacity-70 leading-tight">{sublabel}</span>}
    </button>
  );
}

// ─── ROUTE LIST VIEW ──────────────────────────────────────────────────────────

function RouteListView({
  onCreateNew,
  onOpenRoute,
}: {
  onCreateNew: () => void;
  onOpenRoute: (route: RoutePlan) => void;
}) {
  const { data, isLoading, isError, refetch } = useGetRoutes();
  const deleteRoute = useDeleteRoute();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const routes: RoutePlan[] = useMemo(() => {
    const raw = data as any;
    return raw?.routes || raw?.data?.routes || [];
  }, [data]);

  // Set the active-route flag whenever the user has ANY in-progress route in
  // their list. Means just landing on /routes is enough to protect them from
  // auto-idle/logout — they don't have to drill into the route detail first.
  // Cleared by handleCompleteRoute when they finish, or naturally falls off
  // when there are no in-progress routes left.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasInProgress = routes.some((r) => r.status === "in_progress");
    if (hasInProgress) refreshActiveRouteFlag();
    else clearActiveRouteFlag();
  }, [routes]);

  const statusColors = {
    planned: "bg-amber-50 text-amber-700 border-amber-200",
    in_progress: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  const statusLabels = {
    planned: "Planned",
    in_progress: "In Progress",
    completed: "Completed",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            Routes
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              <InsuraBotIcon className="w-3 h-3" />
              AI Optimized
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            InsuraBot plans the fastest door-knocking route so you spend less time driving
          </p>
        </div>
        <Button onClick={onCreateNew} className="gap-2">
          <Plus className="w-4 h-4" />
          New Route
        </Button>
      </div>

      {isError ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-red-600 mb-3">Failed to load routes</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : routes.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-5">
              <InsuraBotIcon className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Let InsuraBot plan your route
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-2">
              Pick your leads and InsuraBot will calculate the fastest driving sequence —
              no backtracking, no wasted miles. Just the shortest path between every door.
            </p>
            <p className="text-xs text-primary font-medium mb-6 flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" />
              Powered by AI route optimization
            </p>
            <Button onClick={onCreateNew} className="gap-2">
              <Route className="w-4 h-4" />
              Create Your First Route
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {routes.map((route) => (
            <Card
              key={route.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onOpenRoute(route)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center flex-shrink-0">
                      <Route className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {route.name}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-gray-500">
                          {route.stopDetails?.length || 0} stops
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDistance(route.totalDistanceMeters)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDuration(route.totalDurationSeconds)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusColors[route.status]}`}
                    >
                      {statusLabels[route.status]}
                    </span>
                    {deleteConfirmId === route.id ? (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => { deleteRoute.mutate(route.id); setDeleteConfirmId(null); }}
                          title="Permanently delete this route"
                          aria-label="Confirm delete route"
                        >
                          Delete
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => setDeleteConfirmId(null)}
                          title="Keep the route"
                          aria-label="Cancel delete"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(route.id);
                        }}
                        title="Delete this route"
                        aria-label="Delete this route"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ROUTE BUILDER VIEW ───────────────────────────────────────────────────────

// Statuses to exclude from route builder — these leads shouldn't be visited
const EXCLUDED_STATUSES = ["Do not call", "No sale", "Sold & Approved", "Declined", "Bad number"];

/**
 * One page of routable leads.
 *
 * WHY THIS IS NOT "LOAD THE WHOLE BOOK" ANY MORE. It briefly was, and the numbers killed
 * it: measured on prod 2026-08-21, a page of 500 costs ~470ms whether it is page 1 or page
 * 601 (the sort dominates, so deep OFFSET does not degrade — but it never gets cheaper
 * either). Federik Gjoka's 321,047 leads is 643 pages, so simply opening Build Route would
 * have been ~5 minutes of continuous database work for a single page view.
 *
 * So pages are fetched ON DEMAND as the agent scrolls (useInfiniteQuery + the virtualiser
 * below). A 220-lead account costs exactly one request; a 321k-lead account costs one
 * request per 500 rows the agent actually looks at.
 *
 * The filters that decide WHICH leads matter are pushed SERVER-side — state, minAge,
 * maxAge and the search query are all supported by GET /leads. That is what makes paging
 * safe here: filtering "seniors" asks Postgres for seniors, instead of pulling the whole
 * book down and filtering the part that happened to load. Getting that wrong is the
 * original bug — an agent with 2,450 seniors saw a few hundred, because the filter ran
 * over one truncated page.
 */
// 500 is not a preference — it is LeadController.MAX_LIST_LIMIT, the server's hard
// ceiling. Asking for more does not fail, it is silently clamped.
const ROUTE_LEAD_PAGE_SIZE = 500;

interface RouteLeadFilters {
  stateFilter: string;
  minAge: string;
  maxAge: string;
  search: string;
}

async function fetchRoutableLeadPage(
  page: number,
  f: RouteLeadFilters,
): Promise<{ items: any[]; hasMore: boolean }> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(ROUTE_LEAD_PAGE_SIZE),
    lite: "true",
  });
  if (f.stateFilter) params.set("state", f.stateFilter);
  if (f.minAge.trim()) params.set("minAge", f.minAge.trim());
  if (f.maxAge.trim()) params.set("maxAge", f.maxAge.trim());
  if (f.search.trim()) params.set("query", f.search.trim());

  const response = await handleApiCall<any>(async () =>
    api.get<any>({
      url: getApiUrl(`${endpoint.leads.getLeads}?${params.toString()}`),
      auth: true,
    }),
  );

  const raw = response as any;
  const items = raw?.data?.items || raw?.items || raw?.data || [];
  const list = Array.isArray(items) ? items : [];
  // A short page means the server had nothing more to give.
  return { items: list, hasMore: list.length >= ROUTE_LEAD_PAGE_SIZE };
}

const REGIONS: Record<string, { label: string; states: string[] }> = {
  west: { label: "West Coast", states: ["CA", "OR", "WA", "NV", "AZ", "HI", "AK"] },
  mountain: { label: "Mountain", states: ["CO", "UT", "MT", "WY", "ID", "NM"] },
  central: { label: "Central", states: ["TX", "OK", "KS", "NE", "SD", "ND", "MN", "IA", "MO", "AR", "LA", "WI", "IL", "IN", "MI", "OH"] },
  southeast: { label: "Southeast", states: ["FL", "GA", "SC", "NC", "VA", "AL", "MS", "TN", "KY", "WV"] },
  northeast: { label: "Northeast", states: ["NY", "NJ", "PA", "CT", "MA", "RI", "NH", "VT", "ME", "MD", "DE", "DC"] },
};

// Pipeline status badge colors — uses same getStatusColor as leads page

function getDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function RouteBuilderView({
  onBack,
  onRouteCreated,
}: {
  onBack: () => void;
  onRouteCreated: (route: RoutePlan) => void;
}) {
  const [routeName, setRouteName] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [useMyLocation, setUseMyLocation] = useState(false);
  const [myLocation, setMyLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [listFilter, setListFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "distance" | "status" | "tag" | "source" | "age">("name");
  const [radiusMiles, setRadiusMiles] = useState(5);
  const [showPreviewMap, setShowPreviewMap] = useState(false);
  const [pinDrop, setPinDrop] = useState<{ lat: number; lng: number } | null>(null);

  // Server-side filters are debounced: minAge/maxAge/search are typed, and without this
  // every keystroke would start a fresh paged query against the whole book.
  const [debouncedFilters, setDebouncedFilters] = useState({ minAge: "", maxAge: "", search: "" });
  useEffect(() => {
    const id = setTimeout(
      () => setDebouncedFilters({ minAge, maxAge, search: searchQuery }),
      400,
    );
    return () => clearTimeout(id);
  }, [minAge, maxAge, searchQuery]);

  // Virtualised lead list. Every row has the same two-line shape (name + address), so a
  // fixed row height is exact rather than estimated — no measurement pass, no scroll jitter.
  const listParentRef = useRef<HTMLDivElement>(null);

  // O(1) membership for the row renderer. selectedLeadIds.includes() per row is
  // quadratic once a book runs to six figures — virtualising the DOM does not help if
  // every visible row still scans a 1,000-entry array.
  const selectedIdSet = useMemo(() => new Set(selectedLeadIds), [selectedLeadIds]);

  const optimizeRoute = useOptimizeRoute();
  const routeProgress = useRouteProgress();

  // Fetch leads with addresses
  const MAX_STOPS = 1000;

  const {
    data: leadsPages,
    isLoading: leadsLoading,
    isError: leadsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "leads-for-routing",
      stateFilter,
      debouncedFilters.minAge,
      debouncedFilters.maxAge,
      debouncedFilters.search,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchRoutableLeadPage(pageParam as number, {
        stateFilter,
        minAge: debouncedFilters.minAge,
        maxAge: debouncedFilters.maxAge,
        search: debouncedFilters.search,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    staleTime: 60 * 1000,
  });

  const leadsData = useMemo(
    () => (leadsPages?.pages ?? []).flatMap((p: any) => p.items),
    [leadsPages],
  );

  // Get region states for filtering
  const regionStates = regionFilter ? REGIONS[regionFilter]?.states || [] : [];

  const leads: Lead[] = useMemo(() => {
    const items = leadsData;
    return Array.isArray(items)
      ? items.filter((l: Lead) => {
          // Routes are for door-knocking — a lead needs a real STREET address
          // (with a house number) to be routable/geocodable. Skip anything with
          // only city/state or nothing at all (e.g. the "Test City, TX" test
          // rows) — you can't knock on those, and they just pad the list and
          // tank the geocode hit-rate.
          const streetAddr = (l.address || "").trim();
          if (!streetAddr || !/\d/.test(streetAddr)) return false;
          if (l.leadStat && EXCLUDED_STATUSES.includes(l.leadStat)) return false;
          // Region filter (client-side since API only filters by single state)
          if (regionStates.length > 0 && l.state && !regionStates.includes(l.state.toUpperCase())) return false;
          // Radius filter from GPS position or pin drop
          const centerPoint = pinDrop || (useMyLocation ? myLocation : null);
          if (centerPoint && l.latitude && l.longitude && radiusMiles > 0) {
            const dist = getDistanceMiles(centerPoint.lat, centerPoint.lng, l.latitude, l.longitude);
            if (dist > radiusMiles) return false;
          }
          return true;
        })
      : [];
  }, [leadsData, regionStates, useMyLocation, myLocation, radiusMiles, pinDrop]);

  // Get unique list names, pipeline statuses, and tags for filter dropdowns
  const listNames = useMemo(() => [...new Set(leads.map(l => l.listName).filter(Boolean))].sort(), [leads]);
  const pipelineStatuses = useMemo(() => [...new Set(leads.map(l => l.leadStat).filter(Boolean))].sort(), [leads]);
  const availableTags = useMemo(
    () => [...new Set(leads.flatMap(l => Array.isArray(l.tags) ? l.tags : []).filter(Boolean))].sort(),
    [leads],
  );

  const filteredLeads = useMemo(() => {
    let result = leads;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (l) =>
          l.name?.toLowerCase().includes(q) ||
          l.firstName?.toLowerCase().includes(q) ||
          l.lastName?.toLowerCase().includes(q) ||
          l.address?.toLowerCase().includes(q) ||
          l.city?.toLowerCase().includes(q) ||
          l.zipCode?.includes(q),
      );
    }

    // Pipeline filter
    if (pipelineFilter) {
      result = result.filter((l) => l.leadStat === pipelineFilter);
    }

    // Source (list) filter
    if (listFilter) {
      result = result.filter((l) => l.listName === listFilter);
    }

    // Tag filter — lead must carry the selected tag
    if (tagFilter) {
      result = result.filter((l) => Array.isArray(l.tags) && l.tags.includes(tagFilter));
    }

    // Age range filter — keep leads whose age falls within [minAge, maxAge].
    // Age is stored as a string; coerce to a number. When an age bound is set,
    // leads with no usable age are excluded (they can't satisfy the range).
    const ageLo = minAge.trim() === "" ? null : parseInt(minAge, 10);
    const ageHi = maxAge.trim() === "" ? null : parseInt(maxAge, 10);
    if (ageLo !== null || ageHi !== null) {
      result = result.filter((l) => {
        const a = parseInt(String(l.age ?? ""), 10);
        if (!Number.isFinite(a)) return false;
        if (ageLo !== null && a < ageLo) return false;
        if (ageHi !== null && a > ageHi) return false;
        return true;
      });
    }

    // Sort
    const sortCenter = pinDrop || myLocation;
    if (sortBy === "distance" && sortCenter) {
      result = [...result].sort((a, b) => {
        const distA = a.latitude && a.longitude ? getDistanceMiles(sortCenter.lat, sortCenter.lng, a.latitude, a.longitude) : 99999;
        const distB = b.latitude && b.longitude ? getDistanceMiles(sortCenter.lat, sortCenter.lng, b.latitude, b.longitude) : 99999;
        return distA - distB;
      });
    } else if (sortBy === "status") {
      result = [...result].sort((a, b) => (a.leadStat || "").localeCompare(b.leadStat || ""));
    } else if (sortBy === "tag") {
      // Sort by first tag alphabetically (leads without tags sink to the bottom)
      result = [...result].sort((a, b) => {
        const at = Array.isArray(a.tags) && a.tags.length > 0 ? a.tags[0] : "￿";
        const bt = Array.isArray(b.tags) && b.tags.length > 0 ? b.tags[0] : "￿";
        return at.localeCompare(bt);
      });
    } else if (sortBy === "source") {
      result = [...result].sort((a, b) => (a.listName || "￿").localeCompare(b.listName || "￿"));
    } else if (sortBy === "age") {
      // Oldest first — the priority demographic for life/final-expense. Leads with
      // no usable age sink to the bottom.
      result = [...result].sort((a, b) => {
        const aa = parseInt(String(a.age ?? ""), 10);
        const ba = parseInt(String(b.age ?? ""), 10);
        return (Number.isFinite(ba) ? ba : -1) - (Number.isFinite(aa) ? aa : -1);
      });
    } else {
      result = [...result].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return result;
  }, [leads, searchQuery, pipelineFilter, listFilter, tagFilter, minAge, maxAge, sortBy, myLocation, pinDrop]);

  const rowVirtualizer = useVirtualizer({
    count: filteredLeads.length,
    getScrollElement: () => listParentRef.current,
    estimateSize: () => 57,
    overscan: 8,
  });

  // Pull the next page in when the agent scrolls near the end of what is loaded.
  //
  // This is the whole point of paging on demand: a 220-lead account never triggers it, and
  // a 321k-lead account only pays for the rows its owner actually scrolls past — instead of
  // 643 requests (~5 minutes of database time) the moment the screen opens.
  const virtualRows = rowVirtualizer.getVirtualItems();
  useEffect(() => {
    const last = virtualRows[virtualRows.length - 1];
    if (!last) return;
    // 100 rows of runway so the next page lands before the agent reaches the bottom.
    if (last.index >= filteredLeads.length - 100 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [virtualRows, filteredLeads.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const toggleLead = (id: string) => {
    setSelectedLeadIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_STOPS) { showToast(`Maximum ${MAX_STOPS} stops per route`, "error"); return prev; }
      return [...prev, id];
    });
  };

  const selectAll = () => {
    const allIds = filteredLeads.map((l) => l.id);
    setSelectedLeadIds((prev) => {
      const newIds = allIds.filter((id) => !prev.includes(id));
      const combined = [...prev, ...newIds].slice(0, MAX_STOPS);
      if (combined.length === MAX_STOPS && newIds.length > combined.length - prev.length) {
        showToast(`Capped at ${MAX_STOPS} stops`, "info");
      }
      return combined;
    });
  };

  const clearSelection = () => setSelectedLeadIds([]);

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      showToast("Geolocation not supported", "error");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setUseMyLocation(true);
        setSortBy("distance");
        showToast("Location set — leads sorted by distance", "success");
      },
      () => showToast("Could not get location", "error"),
      { enableHighAccuracy: true },
    );
  };

  // Covers the WHOLE route build (geocode phase + optimize mutation) — the
  // button's optimizeRoute.isPending only covers the mutation. State drives the
  // UI; the ref blocks same-tick re-entry before React re-renders.
  const [isBuildingRoute, setIsBuildingRoute] = useState(false);
  const buildingRef = useRef(false);

  const handleOptimize = async () => {
    // DEMO SANDBOX: prompt for the trial instead of optimising.
    //
    // Blocked at the click, not just server-side: the client runs a MINUTES-LONG geocode
    // pass before the request is ever sent, so a backend 403 would land after the visitor
    // had already watched a progress bar crawl — reading as a failure rather than a
    // boundary. The demo's routes already carry real ordered stops, so there is nothing
    // here to generate.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Building and optimizing routes is disabled in the demo sandbox. Start a free trial to plan your own field routes.',
        }),
      );
      return;
    }

    // Re-entrancy guard for the WHOLE build. The client geocode phase runs for
    // minutes BEFORE the optimize mutation fires, so optimizeRoute.isPending is
    // false the entire time — without this, a second tap started a duplicate
    // geocode loop AND created a duplicate route (2× Nominatim load too).
    if (buildingRef.current) return;
    if (selectedLeadIds.length < 2) {
      showToast("Select at least 2 leads", "error");
      return;
    }
    buildingRef.current = true;
    setIsBuildingRoute(true);

    // If no GPS location, don't send start coords — backend will use
    // the first lead in the optimized sequence as the start point.
    // This allows remote planning (e.g., MI agent planning a CA route for next week).
    const startPoint = pinDrop || (useMyLocation && myLocation ? myLocation : null);
    const startLat = startPoint?.lat;
    const startLng = startPoint?.lng;

    // Push to the bottom-right progress widget so the agent can navigate
    // away (call a lead, check appointments) while the optimize runs.
    // The job auto-dismisses 12s after completion; failures stick until
    // manually dismissed.
    const jobId = (typeof crypto !== "undefined" && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : `route-build-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    routeProgress.startRouteJob(jobId, {
      routeName: routeName || `Route (${selectedLeadIds.length} stops)`,
      totalStops: selectedLeadIds.length,
    });
    try {
      // CLIENT-SIDE geocoding — runs from the USER's IP (their browser), NOT the
      // server's. This is the whole point: a big batch can only rate-limit the
      // individual user's IP, never the one shared server IP that would take down
      // routes for everyone. We send the coords to the server, which just persists
      // them (no server-side geocoder call). Nominatim's public policy is max 1
      // req/sec PER IP, so we pace at ~1.1s. Leads that fail to geocode are simply
      // left out of this route.
      const selectedLeads = leads.filter((l) => selectedLeadIds.includes(l.id));
      const needGeocode = selectedLeads.filter(
        (l) => !(Number.isFinite(l.latitude) && Number.isFinite(l.longitude)),
      );
      const geocodes: Array<{ leadId: string; lat: number; lng: number }> = [];

      // FAST PASS — Census bulk geocode (server-side). The Census batch API is
      // built for bulk (10k/call, NO rate limit), so it's the one geocoder we
      // can legally hit with the whole selection at once: ~85-90% of real
      // addresses resolve in seconds and are persisted server-side. Only the
      // misses fall through to the paced 1/sec Nominatim sweep below — turning
      // a ~1,000-lead geocode from ~20 min into seconds + a short tail.
      // If the fast pass fails (older backend / network), we just sweep it all.
      let toSweep = needGeocode;
      if (needGeocode.length > 0) {
        routeProgress.updateRouteJob(jobId, {
          stage: "geocoding",
          message: `Locating ${needGeocode.length} addresses — fast pass…`,
        });
        try {
          const resp: any = await api.post({
            url: getApiUrl(endpoint.routes.geocodeLeads),
            body: { leadIds: needGeocode.map((l) => l.id), censusOnly: true },
            auth: true,
            // Census on a big batch can take a couple of minutes worst-case.
            options: { timeout: 240_000 },
          });
          const data = resp?.data?.data || resp?.data || resp || {};
          if (Array.isArray(data.missingLeadIds)) {
            const missSet = new Set<string>(data.missingLeadIds);
            toSweep = needGeocode.filter((l) => missSet.has(l.id));
          }
        } catch {
          /* fast pass unavailable — fall back to the full client sweep */
        }
      }

      // Vendor address data is mixed: some rows carry a FULL address in the
      // street field (city/state/zip already present), others are street-ONLY.
      // Geocoding a bare street ("9263 Lake Rd") with no city/state matches the
      // same street name in another state — a Dallas lead landed in Minnesota.
      // So: only trust the address AS-IS when it already carries a zip or its
      // state token; otherwise PIN it with city+state+zip. Verified on real TX
      // leads: ~73% land in the CORRECT state, with ZERO wrong-state hits (the
      // old "address-as-is first" was 13% correct — the rest were wrong states).
      const buildQueries = (l: Lead): string[] => {
        const norm = (s?: string) => (s || "").replace(/\s+/g, " ").replace(/,\s*,/g, ",").replace(/,\s*$/, "").trim();
        const addr = norm(l.address);
        const city = norm(l.city);
        const st = /^[A-Za-z]{2}$/.test(norm(l.state)) ? norm(l.state).toUpperCase() : "";
        const zip = (norm(l.zipCode).match(/\b\d{5}\b/) || [""])[0] || (addr.match(/\b\d{5}\b/) || [""])[0];
        const street = addr
          .replace(/\b(APT|UNIT|STE|SUITE|LOT|RM|ROOM|BLDG|FL|FLOOR)\.?\s*\S+/gi, " ")
          .replace(/#\s*\S+/g, " ")
          .replace(/\bP\.?O\.?\s*BOX\s*\d+\b/gi, "")
          .replace(/\s+/g, " ").trim();
        const cityOk = !!city && !/\d/.test(city);
        // Does the address field ALREADY carry location context (a zip, or its
        // state token)? Only then is geocoding it as-is safe.
        const hasContext = /\b\d{5}\b/.test(addr) || (!!st && new RegExp(`(^|[,\\s])${st}(\\b)`).test(addr.toUpperCase()));
        const out: string[] = [];
        if (hasContext) out.push(addr);                          // complete → as-is is safe
        const structured = [street, cityOk ? city : "", st, zip].filter(Boolean).join(", ");
        if (structured) out.push(structured);                    // street-only → PINNED to city/state/zip
        if (cityOk && st) out.push(`${street}, ${city}, ${st}`); // looser (no zip) fallback
        return [...new Set(out.filter(Boolean))];
      };

      for (let i = 0; i < toSweep.length; i++) {
        const lead = toSweep[i];
        routeProgress.updateRouteJob(jobId, {
          stage: "geocoding",
          message: `Geocoding addresses… ${i + 1}/${toSweep.length}`,
        });
        let found = false;
        for (const q of buildQueries(lead)) {
          if (found) break;
          // Match Nominatim's policy: 1 req/sec/IP + honor 429/503 back-off.
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}` +
                  `&format=json&countrycodes=us&limit=1&addressdetails=0`,
                {
                  headers: { "Accept-Language": "en-US" },
                  // Watchdog: without this, ONE stalled connection (dead zone,
                  // captive portal, Nominatim hang) froze the whole build at
                  // "Geocoding i/N" forever. Timeout throws → caught below →
                  // loop moves on to the next query/lead.
                  signal: AbortSignal.timeout(10_000),
                },
              );
              if (res.status === 429 || res.status === 503) {
                const ra = parseInt(res.headers.get("Retry-After") || "", 10);
                const waitMs = Math.min(Math.max(Number.isFinite(ra) ? ra : 2, 1), 10) * 1000;
                routeProgress.updateRouteJob(jobId, {
                  stage: "geocoding",
                  message: `Rate-limited — pausing ${Math.round(waitMs / 1000)}s (${i + 1}/${toSweep.length})`,
                });
                await new Promise((r) => setTimeout(r, waitMs));
                continue; // retry same query
              }
              if (res.ok) {
                const d = (await res.json()) as Array<{ lat: string; lon: string }>;
                const lat = parseFloat(d?.[0]?.lat);
                const lng = parseFloat(d?.[0]?.lon);
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                  geocodes.push({ leadId: lead.id, lat, lng });
                  found = true;
                }
              }
              break; // success or non-retryable → try next query candidate
            } catch {
              break; // network error → try next query candidate
            }
          }
          // Pace every Nominatim call to its 1 req/sec-per-IP limit.
          await new Promise((r) => setTimeout(r, 1100));
        }
      }

      routeProgress.updateRouteJob(jobId, { stage: "optimizing", message: "Optimizing visit order…" });
      const result = await optimizeRoute.mutateAsync({
        leadIds: selectedLeadIds,
        name: routeName || undefined,
        startLat,
        startLng,
        geocodes,
      });

      const route = (result as any)?.route || (result as any)?.data?.route;
      if (route) {
        // Honest stop count: leads whose address couldn't be located are left
        // out of the route — say so instead of silently shrinking the list.
        const built = Array.isArray(route.stopDetails) ? route.stopDetails.length : 0;
        const dropped = Math.max(0, selectedLeadIds.length - built);
        showToast(
          dropped > 0
            ? `Route optimized — ${built} stops mapped, ${dropped} address${dropped === 1 ? "" : "es"} couldn't be located`
            : "Route optimized",
          dropped > 0 ? "info" : "success",
        );
        routeProgress.completeRouteJob(jobId, {
          routeId: route.id,
          stats: {
            distanceMeters: route.totalDistanceMeters,
            durationSeconds: route.totalDurationSeconds,
          },
        });
        onRouteCreated(route);
      } else {
        routeProgress.failRouteJob(jobId, "Couldn't build the route");
      }
    } catch {
      // Error already shown by service
      routeProgress.failRouteJob(jobId, "Couldn't build the route — try again");
    } finally {
      buildingRef.current = false;
      setIsBuildingRoute(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <h2 className="text-lg font-bold text-gray-900">Build Route</h2>
      </div>

      {/* Config row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Route name (optional)"
          value={routeName}
          onChange={(e) => setRouteName(e.target.value)}
          className="sm:max-w-xs"
        />
        <Button
          variant="outline"
          size="sm"
          className={`gap-1.5 ${useMyLocation ? "border-primary/40 bg-primary/5 text-primary" : ""}`}
          onClick={handleGetLocation}
        >
          <LocateFixed className="w-3.5 h-3.5" />
          {useMyLocation ? "Location set" : "Use my location"}
        </Button>
      </div>

      {/* Starting point search */}
      <div className="flex gap-2">
        <Input
          placeholder="Search starting address (hotel, office, etc.)"
          className="text-sm"
          onKeyDown={async (e: any) => {
            if (e.key !== "Enter" || !e.target.value.trim()) return;
            const query = e.target.value.trim();
            try {
              // Nominatim (OpenStreetMap) — free, no key, matches the
              // server-side geocoder we use for leads. countrycodes=us
              // narrows results to the US.
              const url =
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}` +
                `&format=json&countrycodes=us&limit=1&addressdetails=0`;
              const res = await fetch(url, {
                headers: { "Accept-Language": "en-US" },
              });
              const data = (await res.json()) as Array<{
                lat: string;
                lon: string;
                display_name: string;
              }>;
              if (data?.[0]) {
                const lat = parseFloat(data[0].lat);
                const lng = parseFloat(data[0].lon);
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                  setPinDrop({ lat, lng });
                  setSortBy("distance");
                  setShowPreviewMap(true);
                  showToast(`Starting point: ${data[0].display_name}`, "success");
                  return;
                }
              }
              showToast("Address not found", "error");
            } catch {
              showToast("Search failed", "error");
            }
          }}
        />
        {pinDrop && (
          <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => setPinDrop(null)}>
            Clear
          </Button>
        )}
      </div>

      {/* Lead picker */}
      <Card>
        <CardContent className="p-0">
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <Input
              placeholder="Search leads by name, address, city, zip..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="text-sm"
            />
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/30">
            {/* Region filter */}
            <select
              value={regionFilter}
              onChange={(e) => { setRegionFilter(e.target.value); setStateFilter(""); }}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
            >
              <option value="">All Regions</option>
              <option value="west">West Coast</option>
              <option value="mountain">Mountain</option>
              <option value="central">Central</option>
              <option value="southeast">Southeast</option>
              <option value="northeast">Northeast</option>
            </select>

            {/* State filter */}
            <select
              value={stateFilter}
              onChange={(e) => { setStateFilter(e.target.value); setRegionFilter(""); }}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
            >
              <option value="">All States</option>
              {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              value={pipelineFilter}
              onChange={(e) => setPipelineFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
            >
              <option value="">All Pipelines</option>
              {pipelineStatuses.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {listNames.length > 0 && (
              <select
                value={listFilter}
                onChange={(e) => setListFilter(e.target.value)}
                className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
                title="Filter by lead source (upload list)"
              >
                <option value="">All Sources</option>
                {listNames.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            )}

            {availableTags.length > 0 && (
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
                title="Filter by tag"
              >
                <option value="">All Tags</option>
                {availableTags.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}

            {/* Age range filter — narrows the selectable pool; build then optimizes only these */}
            <div className="flex items-center gap-1" title="Filter by age range">
              <span className="text-[10px] text-gray-500">Age</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={120}
                placeholder="min"
                value={minAge}
                onChange={(e) => setMinAge(e.target.value)}
                className="w-16 text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
              />
              <span className="text-gray-400">–</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={120}
                placeholder="max"
                value={maxAge}
                onChange={(e) => setMaxAge(e.target.value)}
                className="w-16 text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
              />
              {(minAge || maxAge) && (
                <button
                  type="button"
                  onClick={() => { setMinAge(""); setMaxAge(""); }}
                  className="text-[11px] text-gray-400 hover:text-gray-600 px-0.5"
                  title="Clear age filter"
                >
                  ✕
                </button>
              )}
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-primary"
            >
              <option value="name">Sort: Name</option>
              <option value="distance" disabled={!myLocation && !pinDrop}>Sort: Nearest{!myLocation && !pinDrop ? " (set location or pin)" : ""}</option>
              <option value="status">Sort: Pipeline</option>
              <option value="age">Sort: Age (oldest)</option>
              <option value="tag" disabled={availableTags.length === 0}>Sort: Tag</option>
              <option value="source" disabled={listNames.length === 0}>Sort: Source</option>
            </select>

            {/* Radius filter — show when GPS or pin drop is active */}
            {(pinDrop || (useMyLocation && myLocation)) && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500">Radius:</span>
                <select
                  value={radiusMiles}
                  onChange={(e) => setRadiusMiles(Number(e.target.value))}
                  className="text-xs border border-gray-200 rounded-md px-1.5 py-1.5 bg-white outline-none focus:border-primary"
                >
                  <option value={1}>1 mi</option>
                  <option value={3}>3 mi</option>
                  <option value={5}>5 mi</option>
                  <option value={10}>10 mi</option>
                  <option value={25}>25 mi</option>
                  <option value={50}>50 mi</option>
                  <option value={100}>100 mi</option>
                  <option value={0}>No limit</option>
                </select>
              </div>
            )}

            <button
              onClick={() => setShowPreviewMap(!showPreviewMap)}
              className={`text-[10px] font-medium px-2 py-1 rounded-md transition-colors ${showPreviewMap ? "bg-primary/10 text-primary" : "text-gray-500 hover:text-gray-700"}`}
            >
              {showPreviewMap ? "Hide Map" : "Show Map"}
            </button>

            <span className="text-[10px] text-gray-400 ml-auto hidden sm:inline">
              DNC, No Sale, Sold auto-excluded
            </span>
          </div>

          {/* Preview map with radius */}
          {showPreviewMap && (
            <div className="h-[450px] border-b border-gray-100">
              <RouteMap
                stops={filteredLeads
                  .filter(l => l.latitude && l.longitude)
                  .slice(0, 200)
                  .map((l, i) => ({
                    leadId: l.id,
                    order: selectedLeadIds.includes(l.id) ? selectedLeadIds.indexOf(l.id) + 1 : 0,
                    name: l.name || `${l.firstName || ""} ${l.lastName || ""}`.trim(),
                    address: l.address || "",
                    city: l.city || "",
                    state: l.state || "",
                    zipCode: l.zipCode || "",
                    latitude: l.latitude!,
                    longitude: l.longitude!,
                    distanceFromPrevMeters: 0,
                    durationFromPrevSeconds: 0,
                    cumulativeDistanceMeters: 0,
                    cumulativeDurationSeconds: 0,
                    visited: false,
                    skipped: false,
                    notHome: false,
                  }))}
                liveTracking={!!myLocation}
                showRadius={!!myLocation && radiusMiles > 0 && !pinDrop}
                radiusMiles={radiusMiles}
                allowPinDrop={true}
                pinDropLocation={pinDrop}
                onPinDrop={(lat, lng) => {
                  if (lat === 0 && lng === 0) {
                    setPinDrop(null); // Clear pin
                  } else {
                    setPinDrop({ lat, lng });
                    setSortBy("distance");
                  }
                }}
                onStopClick={(leadId) => toggleLead(leadId)}
                className="h-full"
              />
            </div>
          )}

          {/* Selection toolbar */}
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50/70 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">
                {selectedLeadIds.length} selected
              </span>
              <button onClick={selectAll} className="text-xs text-primary hover:underline">
                Select all ({filteredLeads.length}{hasNextPage ? " loaded" : ""})
              </button>
              {selectedLeadIds.length > 0 && (
                <button onClick={clearSelection} className="text-xs text-gray-500 hover:underline">
                  Clear
                </button>
              )}
            </div>
            <span className="text-xs text-gray-400">
              {leads.length} leads with addresses
              {hasNextPage && (
                <span className="ml-2 text-gray-500">
                  · {isFetchingNextPage ? "loading more…" : "scroll for more"}
                </span>
              )}
            </span>
          </div>

          {/* Lead list — virtualised; see ROUTE_LEAD_MAX_PAGES for why that matters */}
          <div ref={listParentRef} className="max-h-[400px] overflow-y-auto">
            {leadsError ? (
              <div className="py-12 text-center text-sm text-red-500">
                Failed to load leads. Check your connection.
              </div>
            ) : leadsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">
                No leads with addresses found
              </div>
            ) : (
              <div
                style={{ height: rowVirtualizer.getTotalSize(), width: "100%", position: "relative" }}
              >
              {virtualRows.map((virtualRow) => {
                const lead = filteredLeads[virtualRow.index];
                const isSelected = selectedIdSet.has(lead.id);
                const distCenter = pinDrop || myLocation;
                const distMiles = distCenter && lead.latitude && lead.longitude
                  ? getDistanceMiles(distCenter.lat, distCenter.lng, lead.latitude, lead.longitude)
                  : null;
                const pipelineColor = getStatusColor(lead.leadStat || "");

                return (
                  <div
                    key={lead.id}
                    onClick={() => toggleLead(lead.id)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: virtualRow.size,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors border-b border-gray-100 ${isSelected ? "bg-primary/5" : "hover:bg-gray-50"}`}
                  >
                    <div
                      className={`rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? "bg-primary border-primary" : "border-gray-300"}`}
                      style={{ width: 18, height: 18 }}
                    >
                      {isSelected && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {lead.name || `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "Unknown"}
                        </p>
                        {lead.leadStat && (
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${pipelineColor}`}>
                            {lead.leadStat === "New lead" ? "Leads" : lead.leadStat}
                          </span>
                        )}
                        {lead.age ? (
                          <span className="text-[10px] text-gray-500 font-medium flex-shrink-0 tabular-nums">
                            {lead.age}y
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {[lead.address, lead.city, lead.state, lead.zipCode].filter(Boolean).join(", ")}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {distMiles !== null && (
                        <span className="text-[10px] text-gray-400 font-medium tabular-nums">
                          {distMiles < 1 ? `${(distMiles * 5280).toFixed(0)} ft` : `${distMiles.toFixed(1)} mi`}
                        </span>
                      )}
                      {lead.latitude && (
                        <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full font-medium">
                          GPS
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Optimize button */}
      <Button
        onClick={handleOptimize}
        disabled={selectedLeadIds.length < 2 || isBuildingRoute || optimizeRoute.isPending}
        className="w-full gap-2 h-12 shadow-lg"
        size="lg"
      >
        {isBuildingRoute || optimizeRoute.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {optimizeRoute.isPending
              ? `InsuraBot is optimizing ${selectedLeadIds.length} stops...`
              : `InsuraBot is locating addresses...`}
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            Optimize Route ({selectedLeadIds.length} stops)
          </>
        )}
      </Button>
      {selectedLeadIds.length >= 2 && !isBuildingRoute && !optimizeRoute.isPending && (
        <p className="text-center text-[11px] text-gray-400 -mt-2">
          InsuraBot will geocode addresses, calculate distances, and find the fastest sequence
        </p>
      )}
    </div>
  );
}

// ─── ROUTE DETAIL VIEW ────────────────────────────────────────────────────────

function RouteDetailView({
  route: initialRoute,
  onBack,
}: {
  route: RoutePlan;
  onBack: () => void;
}) {
  const routeProgress = useRouteProgress();
  const [activeStopIndex, setActiveStopIndex] = useState(0);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [quickNote, setQuickNote] = useState("");
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showNoSaleModal, setShowNoSaleModal] = useState(false);
  const [pipelineLeadId, setPipelineLeadId] = useState<string | null>(null);
  const [pipelineLeadName, setPipelineLeadName] = useState<string>("");
  const [pipelineLeadState, setPipelineLeadState] = useState<string>("");
  const [appointmentTimes, setAppointmentTimes] = useState<Record<string, string>>({});
  const updateRoute = useUpdateRoute();

  // Fetch lead detail when a stop is selected
  const { data: leadDetailData } = useQuery({
    queryKey: ["lead-detail-route", selectedLeadId],
    queryFn: async () => {
      if (!selectedLeadId) return null;
      const response = await handleApiCall<any>(async () =>
        api.get<any>({ url: getApiUrl(endpoint.leads.getLeadById(selectedLeadId)), auth: true }),
      );
      return response;
    },
    enabled: !!selectedLeadId,
    staleTime: 30 * 1000,
  });

  const leadDetail = (leadDetailData as any)?.data || leadDetailData;

  // Set/clear active route flag for idle prevention on mount/unmount
  useEffect(() => {
    if (initialRoute.status === "in_progress") {
      refreshActiveRouteFlag();
    }
    return () => {
      // Only clear if we set it (don't clear on back if still in_progress — they might come back)
    };
  }, [initialRoute.status]);

  // Re-fetch for latest data — using consistent query key for cache invalidation
  const { data: routeData } = useQuery({
    queryKey: ["routes", "detail", initialRoute.id],
    queryFn: async () => {
      const response = await handleApiCall<any>(async () =>
        api.get<any>({
          url: getApiUrl(endpoint.routes.getById(initialRoute.id)),
          auth: true,
        }),
      );
      return response;
    },
    placeholderData: { route: initialRoute },
  });

  const route: RoutePlan =
    (routeData as any)?.route ||
    (routeData as any)?.data?.route ||
    initialRoute;

  // Track when agent arrives at each stop
  const stopTimers = useRef<Record<string, number>>({});

  // Auto-advance to next unvisited stop after any action
  const advanceToNextStop = (currentLeadId: string) => {
    // Record time spent at this stop
    const arrivedTime = stopTimers.current[currentLeadId];
    if (arrivedTime) {
      const timeSpentSeconds = Math.round((Date.now() - arrivedTime) / 1000);
      // Fire-and-forget: save time spent to check-in
      api.post({
        url: getApiUrl(endpoint.routes.checkIn(route.id)),
        body: {
          leadId: currentLeadId,
          notes: `Time at door: ${Math.floor(timeSpentSeconds / 60)}m ${timeSpentSeconds % 60}s`,
        },
        auth: true,
      }).catch(() => {});
      delete stopTimers.current[currentLeadId];
    }

    const stops = route.stopDetails || [];
    const currentIdx = stops.findIndex(s => s.leadId === currentLeadId);
    // Find next unvisited/unskipped/not-home stop
    for (let i = currentIdx + 1; i < stops.length; i++) {
      if (!stops[i].visited && !stops[i].skipped && !stops[i].notHome) {
        setActiveStopIndex(i);
        // Start timer for next stop
        stopTimers.current[stops[i].leadId] = Date.now();
        return;
      }
    }

    // No more stops — check if all are done and auto-complete
    const remaining = stops.filter(s => !s.visited && !s.skipped && !s.notHome && s.leadId !== currentLeadId);
    if (remaining.length === 0 && route.status === "in_progress") {
      setTimeout(() => {
        showToast("All stops done — route completed!", "success");
        handleCompleteRoute();
      }, 500);
    }
  };

  // Start timer for first stop when route starts
  useEffect(() => {
    if (route.status === "in_progress" && route.stopDetails?.length > 0) {
      const firstUnvisited = route.stopDetails.find(s => !s.visited && !s.skipped && !s.notHome);
      if (firstUnvisited && !stopTimers.current[firstUnvisited.leadId]) {
        stopTimers.current[firstUnvisited.leadId] = Date.now();
      }
    }
  }, [route.status, route.stopDetails]);

  // Single-GPS-source plumbing: RouteMap's watchPosition is the ONLY GPS
  // consumer on this screen; every fix it gets is mirrored into this ref
  // (write-only — no re-render). The 15s ping loop below READS the ref instead
  // of running its own high-accuracy getCurrentPosition — halves GPS battery
  // drain while driving.
  const lastGpsRef = useRef<{
    lat: number; lng: number;
    accuracy?: number | null; speed?: number | null; heading?: number | null;
    at: number;
  } | null>(null);
  const handleMapPositionUpdate = useCallback(
    (pos: { lat: number; lng: number; accuracy?: number | null; speed?: number | null; heading?: number | null }) => {
      lastGpsRef.current = { ...pos, at: Date.now() };
    },
    [],
  );
  // Latest position that FAILED to send (dead zone / offline). Only the newest
  // matters — the backend stores a single last_known_position — so this is a
  // 1-slot buffer flushed the instant connectivity returns.
  const pendingPingRef = useRef<Record<string, number | null | undefined> | null>(null);

  // Live GPS ping — while the route is in_progress, broadcast position every
  // 15s so agency owners see the agent move in real time on the Agency Center
  // live map. Stops cleanly on unmount or when status changes to completed.
  useEffect(() => {
    if (route.status !== "in_progress") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    const postPosition = (body: Record<string, number | null | undefined>) => {
      // Offline: don't fire a doomed request — buffer the newest position and
      // flush on the 'online' event below.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        pendingPingRef.current = body;
        return;
      }
      api
        .post({ url: getApiUrl(`routes/${route.id}/position`), body, auth: true })
        .then(() => { pendingPingRef.current = null; })
        .catch(() => { pendingPingRef.current = body; }); // keep for replay, don't nag the agent
    };

    const sendPing = () => {
      // Refresh the active-route flag — proves the agent is genuinely
      // driving (or at least keeping the route page alive). Without this
      // the time-bounded flag would expire after 30 min even mid-route.
      refreshActiveRouteFlag();
      const gps = lastGpsRef.current;
      if (gps && Date.now() - gps.at < 60_000) {
        // Fresh fix from the map's watch — no extra GPS wake-up needed.
        postPosition({ lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy, speed: gps.speed, heading: gps.heading });
        return;
      }
      // Fallback (map watch hasn't produced a fix yet — mount/GPS warm-up).
      navigator.geolocation.getCurrentPosition(
        (pos) => postPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,       // m/s — driving/walking detection
          heading: pos.coords.heading,   // compass degrees — for future arrow pin
        }),
        () => {},
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 8000 },
      );
    };

    // Flush the buffered position the instant signal returns, so the agency
    // live map catches up immediately instead of waiting for the next tick.
    const onOnline = () => {
      const p = pendingPingRef.current;
      if (p) { pendingPingRef.current = null; postPosition(p); }
    };
    window.addEventListener("online", onOnline);

    // Ping immediately, then every 15s
    sendPing();
    const interval = window.setInterval(sendPing, 15000);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", onOnline);
    };
  }, [route.status, route.id]);

  // Keep the screen awake while a route is actively driven. Without this the
  // phone locking suspends the tab — the GPS watch and the 15s pings die
  // exactly while the agent is on the road. Same Wake Lock pattern as active
  // Twilio calls; the OS auto-releases on tab hide, so re-acquire on return.
  useEffect(() => {
    if (route.status !== "in_progress") return;
    const nav: any = typeof navigator !== "undefined" ? (navigator as any) : null;
    if (!nav || !("wakeLock" in nav)) return;
    let lock: any = null;
    let disposed = false;
    const acquire = async () => {
      try {
        lock = await nav.wakeLock.request("screen");
      } catch {
        /* unsupported or low-battery mode — degrade gracefully */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !disposed) void acquire();
    };
    void acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      try { lock?.release?.(); } catch { /* noop */ }
    };
  }, [route.status]);

  // outcome tags the door_knock so metrics separate appointment-setting from
  // closing. Default 'refused' = a plain knock (answered, no appointment).
  const handleMarkVisited = (
    leadId: string,
    outcome: 'refused' | 'no_sale' | 'sold' = 'refused',
  ) => {
    updateRoute.mutate({
      routeId: route.id,
      data: { leadId, visited: true, skipped: false, outcome },
    });

    // Write to lead notes
    const now = new Date().toLocaleString();
    const label =
      outcome === 'sold'
        ? 'Sold'
        : outcome === 'no_sale'
          ? 'No Sale'
          : 'Refused Appointment';
    api.patch({
      url: getApiUrl(endpoint.leads.updateLead(leadId)),
      body: { notes: `[Route] Door knocked — ${label} — ${now}` },
      auth: true,
    }).catch(() => {});

    advanceToNextStop(leadId);
  };

  const handleMarkSkipped = (leadId: string) => {
    updateRoute.mutate({
      routeId: route.id,
      data: { leadId, visited: false, skipped: true },
    });
    advanceToNextStop(leadId);
  };

  const handleMarkNotHome = (leadId: string) => {
    api.post({
      url: getApiUrl(endpoint.routes.notHome(route.id)),
      body: { leadId },
      auth: true,
    }).then(() => {
      showToast("Marked not home", "info");
      updateRoute.mutate({ routeId: route.id, data: { status: route.status } });
    }).catch(() => showToast("Failed to mark not home", "error"));

    // Write to lead notes
    const now = new Date().toLocaleString();
    api.patch({
      url: getApiUrl(endpoint.leads.updateLead(leadId)),
      body: { notes: `[Route] Not Home — ${now}` },
      auth: true,
    }).catch(() => {});

    advanceToNextStop(leadId);
  };

  const handleSetFieldTag = (leadId: string, tag: string) => {
    api.post({
      url: getApiUrl(endpoint.routes.fieldTag(route.id)),
      body: { leadId, tag },
      auth: true,
    }).then(() => {
      showToast(`Tagged: ${tag}`, "success");
      updateRoute.mutate({ routeId: route.id, data: { status: route.status } });
    }).catch(() => showToast("Failed to set tag", "error"));

    // Sync tag to the lead's tags array (shows in lead details page)
    api.get<any>({
      url: getApiUrl(endpoint.leads.getLeadById(leadId)),
      auth: true,
    }).then((res) => {
      const existingTags: string[] = res?.data?.data?.tags || res?.data?.tags || [];
      if (!existingTags.includes(tag) && existingTags.length < 5) {
        api.patch({
          url: getApiUrl(endpoint.leads.updateLead(leadId)),
          body: { tags: [...existingTags, tag] },
          auth: true,
        }).catch(() => {});
      }
    }).catch(() => {});

    // Write to lead notes
    const now = new Date().toLocaleString();
    api.patch({
      url: getApiUrl(endpoint.leads.updateLead(leadId)),
      body: { notes: `[Route] Tagged: ${tag} — ${now}` },
      auth: true,
    }).catch(() => {});
  };

  // Fetch daily knock stats
  const { data: knockStatsData } = useQuery({
    queryKey: ["knock-stats", new Date().toDateString()],
    queryFn: async () => {
      const response = await handleApiCall<any>(async () =>
        api.get<any>({ url: getApiUrl(endpoint.routes.knockStats), auth: true }),
      );
      return response;
    },
    staleTime: 30 * 1000,
  });

  const knockStats = (knockStatsData as any)?.data || knockStatsData;

  const handleStartRoute = () => {
    // Prevent idle/auto-logout while agent is driving (shared across all tabs).
    refreshActiveRouteFlag();

    // Set status to in_progress FIRST, then re-optimize from GPS
    updateRoute.mutate({
      routeId: route.id,
      data: { status: "in_progress" },
    });

    // Re-optimize from current GPS location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          api.post({
            url: getApiUrl(endpoint.routes.reOptimize(route.id)),
            body: { currentLat: pos.coords.latitude, currentLng: pos.coords.longitude },
            auth: true,
          }).then(() => {
            // Refetch route to get updated geometry
            updateRoute.mutate({ routeId: route.id, data: { status: "in_progress" } });
          }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 5000 },
      );
    }
  };

  const handleCompleteRoute = () => {
    // Clear the active route flag — allow idle/logout again
    clearActiveRouteFlag();
    updateRoute.mutate({
      routeId: route.id,
      data: { status: "completed" },
    });
  };

  // Re-optimize current stop set. Triggers backend geocoding upgrade pass too —
  // any leads previously stored with Census-interpolated coords get re-resolved
  // to rooftop-accurate Nominatim coords, so pins land on the actual house.
  const queryClient = useQueryClient();
  const [reoptimizing, setReoptimizing] = useState(false);
  const [showAddLeads, setShowAddLeads] = useState(false);
  const [showSendToPhone, setShowSendToPhone] = useState(false);
  const [travelMode, setTravelMode] = useState<"driving" | "walking">("driving");
  // Disposition dock is opt-in — only appears when the agent taps a stop
  // circle on the map or a row in the side list. Hidden by default so the
  // map isn't permanently covered by the bottom bar.
  const [dispositionStopId, setDispositionStopId] = useState<string | null>(null);
  // Pin-edit mode — when set, that stop's marker becomes draggable on the
  // map. On drop we PATCH the lead's lat/lng so the corrected position
  // sticks for every future route.
  const [editingPinForLeadId, setEditingPinForLeadId] = useState<string | null>(null);
  // Manual-address override fields, shown next to the dragging pin so the
  // agent can type the correct address when the pin is just plain wrong.
  const [correctAddressForm, setCorrectAddressForm] = useState({
    address: "", city: "", state: "", zipCode: "",
  });
  const [correctingAddress, setCorrectingAddress] = useState(false);
  // Reset the form whenever the agent picks a different lead to edit, and
  // pre-fill it with that lead's current address fields so they only have
  // to fix what's actually wrong.
  useEffect(() => {
    if (!editingPinForLeadId) {
      setCorrectAddressForm({ address: "", city: "", state: "", zipCode: "" });
      return;
    }
    const stop = route?.stopDetails?.find((s) => s.leadId === editingPinForLeadId);
    if (stop) {
      setCorrectAddressForm({
        address: (stop as any).address || "",
        city: (stop as any).city || "",
        state: (stop as any).state || "",
        zipCode: (stop as any).zipCode || "",
      });
    }
  }, [editingPinForLeadId, route?.stopDetails]);

  const handleCorrectAddress = async () => {
    if (!editingPinForLeadId) return;
    setCorrectingAddress(true);
    try {
      const result = await api.patch<{ latitude: number; longitude: number; source: string }>({
        url: getApiUrl(endpoint.routes.correctAddress(editingPinForLeadId)),
        body: correctAddressForm,
        auth: true,
      });
      const lat = (result as any)?.latitude;
      const lng = (result as any)?.longitude;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        queryClient.setQueryData(
          ["routes", "detail", route.id],
          (old: any) => {
            if (!old) return old;
            const r = old.route || old.data?.route;
            if (!r?.stopDetails) return old;
            const updated = r.stopDetails.map((s: any) =>
              s.leadId === editingPinForLeadId
                ? { ...s, latitude: lat, longitude: lng, geocodeSource: "manual", ...correctAddressForm }
                : s,
            );
            if (old.route) return { ...old, route: { ...r, stopDetails: updated } };
            return { ...old, data: { ...old.data, route: { ...r, stopDetails: updated } } };
          },
        );
      }
      await queryClient.invalidateQueries({ queryKey: ["routes", "detail", route.id] });
      showToast("Address corrected — re-routing…", "success");
      setEditingPinForLeadId(null);
      callReOptimize().catch(() => { /* toast already surfaced inside */ });
    } catch {
      // Don't surface raw backend / service messages to the agent — they'd
      // include text like "Census timeout" or upstream HTTP codes that
      // mean nothing to a door-knocker. Generic, action-oriented copy.
      showToast("Couldn't find that address — try adding city or ZIP", "error");
    } finally {
      setCorrectingAddress(false);
    }
  };

  const handlePinDragEnd = async (leadId: string, lat: number, lng: number) => {
    try {
      // Hit the route-aware endpoint instead of the generic lead PATCH —
      // it updates the lead row AND syncs every active route's stopDetails
      // JSON, so the corrected coords actually stick after refetch.
      // Generic lead PATCH only updates the leads row, leaving stale
      // stopDetails coords that snap the pin back on next render.
      await api.patch({
        url: getApiUrl(endpoint.routes.saveManualPin(leadId)),
        body: { latitude: lat, longitude: lng },
        auth: true,
      });
      // Reflect immediately by patching the in-memory route stopDetails;
      // also invalidate the route detail query so a fresh fetch confirms.
      queryClient.setQueryData(
        ["routes", "detail", route.id],
        (old: any) => {
          if (!old) return old;
          const r = old.route || old.data?.route;
          if (!r?.stopDetails) return old;
          const updated = r.stopDetails.map((s: any) =>
            s.leadId === leadId
              ? { ...s, latitude: lat, longitude: lng, geocodeSource: "manual" }
              : s,
          );
          if (old.route) return { ...old, route: { ...r, stopDetails: updated } };
          return { ...old, data: { ...old.data, route: { ...r, stopDetails: updated } } };
        },
      );
      await queryClient.invalidateQueries({ queryKey: ["routes", "detail", route.id] });
      showToast("Pin saved — re-routing…", "success");
      setEditingPinForLeadId(null);
      // Auto re-optimize so the polyline and leg distances reflect the
      // corrected pin. For small drags (<100m) the visit order won't change
      // — TSP will return the same sequence — but the polyline geometry
      // will now go to the right house. For larger relocations the order
      // may shift, which is the correct behaviour anyway.
      callReOptimize().catch(() => { /* toast already surfaced inside */ });
    } catch {
      // Generic copy — never surface raw backend errors to the agent.
      showToast("Couldn't save the pin — try again in a moment", "error");
    }
  };

  const callReOptimize = async (addLeadIds?: string[], overrideMode?: "driving" | "walking") => {
    // Same reasoning as handleOptimize — and placed before setReoptimizing, or the UI
    // would drop into its "optimizing…" state behind the prompt and stay there.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Re-optimizing routes is disabled in the demo sandbox. Start a free trial to plan your own field routes.',
        }),
      );
      return;
    }

    setReoptimizing(true);
    // Push a job into the global progress widget so the agent can navigate
    // away (e.g. to call a lead while the route recomputes) and still see
    // the optimization running in the bottom-right corner.
    const jobId = (typeof crypto !== "undefined" && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : `route-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const totalStops = (route.stopDetails?.length || 0) + (addLeadIds?.length || 0);
    routeProgress.startRouteJob(jobId, {
      routeName: route.name || "Route",
      totalStops,
    });
    try {
      const body: Record<string, unknown> = {};
      if (addLeadIds?.length) body.addLeadIds = addLeadIds;
      body.mode = overrideMode || travelMode;

      // Capture live GPS so the new route starts from where the agent is.
      // Surfaces the result so the user knows whether their location was
      // actually used (silent failure made it look like nothing happened).
      let gotLocation = false;
      if (typeof navigator !== "undefined" && navigator.geolocation) {
        showToast("Getting your location…", "info");
        await new Promise<void>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              body.currentLat = pos.coords.latitude;
              body.currentLng = pos.coords.longitude;
              gotLocation = true;
              resolve();
            },
            (err) => {
              if (err.code === err.PERMISSION_DENIED) {
                showToast("Location permission denied — re-optimizing without your start point", "info");
              } else if (err.code === err.TIMEOUT) {
                showToast("Couldn't get GPS in time — re-optimizing without your start point", "info");
              } else {
                showToast("Location unavailable — re-optimizing without your start point", "info");
              }
              resolve();
            },
            { enableHighAccuracy: true, timeout: 6000, maximumAge: 5000 },
          );
        });
      }

      // Advance the widget through the stages so the bar visibly moves.
      // No streaming progress from the backend, so these are anchor
      // points that match the typical pipeline timing (geocode → TSP →
      // directions polyline).
      routeProgress.updateRouteJob(jobId, { stage: "optimizing", message: "Optimizing visit order…" });

      const resp = await api.post<any>({
        url: getApiUrl(endpoint.routes.reOptimize(route.id)),
        body,
        auth: true,
      });
      routeProgress.updateRouteJob(jobId, { stage: "rendering", message: "Drawing the route…" });
      await queryClient.invalidateQueries({ queryKey: ["routes", "detail", route.id] });
      await queryClient.invalidateQueries({ queryKey: ["routes"] });
      const msg = addLeadIds?.length
        ? `${addLeadIds.length} leads added & route re-optimized`
        : gotLocation
          ? "Route re-optimized from your current location"
          : "Route re-optimized";
      showToast(msg, "success");
      const updatedRoute = (resp as any)?.route || (resp as any)?.data?.route;
      routeProgress.completeRouteJob(jobId, {
        routeId: route.id,
        stats: {
          distanceMeters: updatedRoute?.totalDistanceMeters,
          durationSeconds: updatedRoute?.totalDurationSeconds,
        },
      });
    } catch {
      showToast("Couldn't re-optimize the route — try again", "error");
      routeProgress.failRouteJob(jobId, "Couldn't re-optimize this route");
    } finally {
      setReoptimizing(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
            title="Back to route list"
            aria-label="Back to route list"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h2 className="text-lg font-bold text-gray-900 truncate flex items-center gap-2">
            {route.name}
            <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary flex-shrink-0">
              <InsuraBotIcon className="w-2.5 h-2.5" />
              AI
            </span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {route.status !== "completed" && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowSendToPhone(true)}
                className="gap-1.5"
                title="Generate a QR code to open this route in Apple Maps or Google Maps on your phone"
                aria-label="Send this route to your phone"
              >
                <Smartphone className="w-3.5 h-3.5" />
                Send to Phone
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddLeads(true)}
                disabled={reoptimizing}
                className="gap-1.5"
                title="Add more leads to this route — they'll be slotted in based on location"
                aria-label="Add more leads to this route"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Add Leads
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => callReOptimize()}
                disabled={reoptimizing}
                className="gap-1.5"
                title="Recompute the best visit order using your current GPS as the start point"
                aria-label="Re-optimize the route from your current location"
              >
                {reoptimizing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RotateCw className="w-3.5 h-3.5" />
                )}
                Re-optimize
              </Button>
            </>
          )}
          {route.status === "planned" && (
            <Button
              size="sm"
              onClick={handleStartRoute}
              className="gap-1.5"
              title="Start the route — turns on live GPS tracking"
              aria-label="Start route and begin live tracking"
            >
              <Play className="w-3.5 h-3.5" />
              Start Route
            </Button>
          )}
          {route.status === "in_progress" && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCompleteRoute}
              className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              title="Mark this route as completed"
              aria-label="Complete route"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Complete
            </Button>
          )}
        </div>
      </div>

      {/* Map + Stop list split */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-3 h-[calc(100vh-180px)]">
        {/* Map (with disposition dock overlaid for in-progress routes) */}
        <div className="relative h-full min-h-[400px]">
          <RouteMap
            stops={route.stopDetails || []}
            routeGeometry={route.routeGeometry}
            activeStopIndex={activeStopIndex}
            activeStopLeadId={route.stopDetails?.[activeStopIndex]?.leadId || null}
            liveTracking={route.status === "in_progress"}
            onPositionUpdate={handleMapPositionUpdate}
            onStopClick={(leadId) => {
              const idx = route.stopDetails?.findIndex(
                (s) => s.leadId === leadId,
              );
              if (idx !== undefined && idx >= 0) {
                setActiveStopIndex(idx);
                // Tapping a stop circle opens the disposition dock for it.
                setDispositionStopId(leadId);
              }
            }}
            onMapBackgroundClick={() => {
              setDispositionStopId(null);
              setEditingPinForLeadId(null);
            }}
            travelMode={route.status !== "completed" ? travelMode : undefined}
            onTravelModeChange={(mode) => {
              setTravelMode(mode);
              callReOptimize(undefined, mode);
            }}
            travelModeDisabled={reoptimizing}
            editingPinForLeadId={editingPinForLeadId}
            onPinDragEnd={handlePinDragEnd}
            className="h-full min-h-[400px]"
          />
          {route.status === "in_progress" && route.stopDetails && dispositionStopId && (() => {
            const activeStop = route.stopDetails.find((s) => s.leadId === dispositionStopId);
            if (!activeStop) return null;
            const dockIndex = route.stopDetails.findIndex((s) => s.leadId === dispositionStopId);
            const closeDock = () => setDispositionStopId(null);
            const fireUpdatePipeline = (status: string) => {
              const leadId = activeStop.leadId;
              const leadName = (activeStop as any).name || "";
              const leadState = (activeStop as any).state || "";
              if (status === "Appt") {
                setPipelineLeadId(leadId);
                setPipelineLeadName(leadName);
                setPipelineLeadState(leadState);
                setShowAppointmentDialog(true);
                closeDock();
                return;
              }
              if (status === "No sale") {
                setPipelineLeadId(leadId);
                setShowNoSaleModal(true);
                closeDock();
                return;
              }
              api.patch({
                url: getApiUrl(endpoint.leads.updateLead(leadId)),
                body: { leadStat: status },
                auth: true,
              }).then(() => {
                showToast(`Pipeline updated to ${status === "Sold & Approved" ? "Sold" : status}`, "success");
                // Map the pipeline disposition to a door outcome so the knock is
                // tagged for the setting-vs-closing metrics: a close (sold/UW) →
                // 'sold', a decline (they were presented and passed) → 'no_sale',
                // anything else → 'refused' (default).
                const knockOutcome: 'refused' | 'no_sale' | 'sold' =
                  status === "Sold & Approved" || status === "Underwriting"
                    ? "sold"
                    : status === "Declined"
                      ? "no_sale"
                      : "refused";
                handleMarkVisited(leadId, knockOutcome);
                closeDock();
              }).catch(() => {
                showToast("Failed to update pipeline", "error");
              });
            };
            const stopName = (activeStop as any).name || "this stop";
            return (
              <div
                className="absolute bottom-3 left-3 right-3 z-10 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200 px-3 py-2.5"
                // Stop ALL pointer events from reaching the map underneath —
                // prevents any underlying map handler (pan, marker click) from
                // mistakenly registering and dismissing the dock. Dock only
                // closes via the × button or completing a disposition action.
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Now at</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{stopName}</p>
                  </div>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary flex-shrink-0">
                    Stop {dockIndex + 1}
                  </span>
                  <button
                    type="button"
                    onClick={closeDock}
                    className="ml-2 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
                    title="Close"
                    aria-label="Close disposition panel"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                  <DispositionButton
                    icon={<DoorOpen className="w-4 h-4" />}
                    label="Refused"
                    sublabel="No appt"
                    color="emerald"
                    onClick={() => { handleMarkVisited(activeStop.leadId); closeDock(); }}
                  />
                  <DispositionButton
                    icon={<Home className="w-4 h-4" />}
                    label="Not home"
                    color="amber"
                    onClick={() => { handleMarkNotHome(activeStop.leadId); closeDock(); }}
                  />
                  <DispositionButton
                    icon={<SkipForward className="w-4 h-4" />}
                    label="Skip"
                    color="slate"
                    onClick={() => { handleMarkSkipped(activeStop.leadId); closeDock(); }}
                  />
                  <DispositionButton
                    icon={<CalendarPlus className="w-4 h-4" />}
                    label="Appt"
                    color="blue"
                    onClick={() => fireUpdatePipeline("Appt")}
                  />
                  <DispositionButton
                    icon={<Trophy className="w-4 h-4" />}
                    label="Sold"
                    color="violet"
                    onClick={() => fireUpdatePipeline("Sold & Approved")}
                  />
                  <DispositionButton
                    icon={<ThumbsDown className="w-4 h-4" />}
                    label="No sale"
                    color="rose"
                    onClick={() => fireUpdatePipeline("No sale")}
                  />
                </div>
              </div>
            );
          })()}

          {/* Pin-edit overlay: shown while a pin is being dragged so the
              agent can also type the correct address if dragging alone
              isn't enough. Backend re-geocodes via the free OSM/TIGER
              stack and snaps the pin to the new coords. */}
          {editingPinForLeadId && (
            <div className="absolute top-3 left-3 right-3 lg:right-auto lg:max-w-md z-30 bg-white border border-amber-200 rounded-xl shadow-xl p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="text-[13px] font-semibold text-gray-900">Fix this pin</div>
                  <div className="text-[11px] text-gray-500 leading-tight mt-0.5">
                    Drag the highlighted pin OR type the correct address below.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingPinForLeadId(null)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Cancel pin edit"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-1.5">
                <input
                  type="text"
                  placeholder="Street address"
                  value={correctAddressForm.address}
                  onChange={(e) => setCorrectAddressForm((f) => ({ ...f, address: e.target.value }))}
                  className="col-span-1 sm:col-span-3 px-2.5 py-1.5 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <input
                  type="text"
                  placeholder="City"
                  value={correctAddressForm.city}
                  onChange={(e) => setCorrectAddressForm((f) => ({ ...f, city: e.target.value }))}
                  className="px-2.5 py-1.5 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <input
                  type="text"
                  placeholder="State"
                  value={correctAddressForm.state}
                  onChange={(e) => setCorrectAddressForm((f) => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))}
                  className="w-14 px-2.5 py-1.5 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <input
                  type="text"
                  placeholder="ZIP"
                  value={correctAddressForm.zipCode}
                  onChange={(e) => setCorrectAddressForm((f) => ({ ...f, zipCode: e.target.value.replace(/[^0-9-]/g, "").slice(0, 10) }))}
                  className="w-20 px-2.5 py-1.5 text-[12px] border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
              <div className="flex items-center justify-end gap-1.5 mt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingPinForLeadId(null)}
                  className="text-[11px] h-7 px-2"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCorrectAddress}
                  disabled={correctingAddress || !correctAddressForm.address.trim()}
                  className="text-[11px] h-7 px-2 gap-1"
                >
                  {correctingAddress ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {correctingAddress ? "Geocoding…" : "Use this address"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Stop list sidebar */}
        <Card className="overflow-hidden h-full">
          <RouteLeadList
            stops={route.stopDetails || []}
            activeStopIndex={activeStopIndex}
            editingPinForLeadId={editingPinForLeadId}
            onStopClick={(leadId, index) => {
              // Row body tap → just select the stop on the map. Doesn't
              // open the lead detail panel — that's reserved for the eye
              // icon (onViewLead) so a casual row-tap doesn't accidentally
              // pop up a full-screen panel.
              setActiveStopIndex(index);
              if (route.status === "in_progress") {
                setDispositionStopId(leadId);
              }
            }}
            onViewLead={(leadId, index) => {
              setActiveStopIndex(index);
              setSelectedLeadId(leadId);
            }}
            onMarkVisited={handleMarkVisited}
            onMarkSkipped={handleMarkSkipped}
            onMarkNotHome={handleMarkNotHome}
            onSetFieldTag={handleSetFieldTag}
            onAutoFixPin={async (leadId) => {
              try {
                const resp = await api.post<any>({
                  url: getApiUrl(endpoint.routes.autoFixPin(leadId)),
                  body: {},
                  auth: true,
                });
                const data = (resp as any)?.data || resp;
                const confidence = data?.confidence;
                if (confidence === "high" || confidence === "medium") {
                  await queryClient.invalidateQueries({ queryKey: ["routes", "detail", route.id] });
                  showToast("Pin re-checked — drag it on the map to fine-tune if needed", "info");
                } else {
                  showToast("Couldn't pinpoint the address — drag the pin to the right house", "info");
                }
                // Always activate drag mode so agent can verify or correct
                // visually on the satellite view. Free-tier geocoders bottom
                // out around 88-92% accuracy on rural addresses; the agent
                // eyeballing on the map is more reliable than any automated
                // free fix for that last 8-12% of cases.
                setEditingPinForLeadId(leadId);
                return true;
              } catch {
                showToast("Couldn't re-check that pin — try again", "error");
                return false;
              }
            }}
            knockStats={knockStats}
            onUpdatePipeline={(leadId, status) => {
              // Find lead info from route stops
              const stop = (route.stopDetails || []).find((s: any) => s.leadId === leadId);
              const leadName = stop?.name || "";
              const leadState = stop?.state || "";

              // Show appointment dialog for Appt status
              if (status === "Appt") {
                setPipelineLeadId(leadId);
                setPipelineLeadName(leadName);
                setPipelineLeadState(leadState);
                setShowAppointmentDialog(true);
                return;
              }

              // Show no sale reason modal
              if (status === "No sale") {
                setPipelineLeadId(leadId);
                setShowNoSaleModal(true);
                return;
              }

              // Update lead pipeline in CRM
              api.patch({
                url: getApiUrl(endpoint.leads.updateLead(leadId)),
                body: { leadStat: status },
                auth: true,
              }).then(() => {
                showToast(`Pipeline updated to ${status === "Sold & Approved" ? "Sold" : status}`, "success");
              }).catch(() => {
                showToast("Failed to update pipeline", "error");
              });
            }}
            totalDistanceMeters={route.totalDistanceMeters}
            totalDurationSeconds={route.totalDurationSeconds}
            routeStatus={route.status}
            startedAt={route.startedAt}
            completedAt={route.completedAt}
            appointmentTimes={appointmentTimes}
          />
        </Card>
      </div>

      {/* Lead detail full-screen modal — uses same LeadDetailsPanel as Lead Assignment */}
      {selectedLeadId && (
        <div className="fixed inset-0 z-40 bg-primary/30 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setSelectedLeadId(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-20 bg-white rounded-t-2xl flex items-center justify-end px-4 py-3 border-b border-gray-100">
              <button onClick={() => setSelectedLeadId(null)} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <LeadDetailsPanel
              leadId={selectedLeadId}
              onClose={() => setSelectedLeadId(null)}
              compact={true}
              showCloseButton={false}
              alwaysEditing={false}
            />
          </div>
        </div>
      )}

      {/* Appointment Dialog — shown when pipeline set to "Appt" */}
      <NewAppointmentDialog
        open={showAppointmentDialog}
        onOpenChange={(open) => {
          setShowAppointmentDialog(open);
          if (!open) setPipelineLeadId(null);
        }}
        preselectedLeadId={pipelineLeadId || undefined}
        preselectedLeadName={pipelineLeadName || undefined}
        preselectedLeadState={pipelineLeadState || undefined}
        onSave={(appt: any) => {
          // Capture appointment time when created
          const leadId = pipelineLeadId;
          const startTime = appt?.startTime || appt?.apptDate;
          if (leadId && startTime) {
            setAppointmentTimes(prev => ({ ...prev, [leadId]: typeof startTime === 'string' ? startTime : new Date(startTime).toISOString() }));
          }
        }}
        onStatusChange={(leadId: string, status: string) => {
          api.patch({
            url: getApiUrl(endpoint.leads.updateLead(leadId)),
            body: { leadStat: status },
            auth: true,
          }).then(() => {
            showToast("Appointment set & pipeline updated", "success");
          }).catch(() => {
            showToast("Failed to update pipeline", "error");
          });
        }}
      />

      {/* No Sale Reason Modal — shown when a door disposition is set to "No sale".
          Door-knock-specific reasons (agent got in and presented, but no app):
          separates objection-handling misses from product-fit / underwriting. */}
      <NoSaleReasonModal
        isOpen={showNoSaleModal}
        title="Why no sale at the door?"
        description="They let the agent present but didn't apply. Pick the main reason so we can tell objection-handling from product-fit."
        reasons={[
          "Price/premium too high",
          "Spouse / decision-maker not home",
          "Wants to think it over",
          "Didn't qualify (health / underwriting)",
          "Already covered elsewhere",
          "Needs to check budget / call back",
        ]}
        onOpenChange={(open) => {
          setShowNoSaleModal(open);
          if (!open) setPipelineLeadId(null);
        }}
        onConfirm={async (reason: string) => {
          if (!pipelineLeadId) return;
          const leadId = pipelineLeadId;
          await api.patch({
            url: getApiUrl(endpoint.leads.updateLead(leadId)),
            body: { leadStat: "No sale", noSaleReason: reason },
            auth: true,
          });
          showToast("Pipeline updated to No Sale", "success");
          // Record the door as a knock with a 'no_sale' outcome — they were
          // presented to but didn't buy (feeds the close-rate metric).
          handleMarkVisited(leadId, "no_sale");
          setShowNoSaleModal(false);
          setPipelineLeadId(null);
        }}
      />

      {showAddLeads && (
        <AddLeadsModal
          existingLeadIds={(route.stopDetails || []).map((s: any) => s.leadId)}
          onClose={() => setShowAddLeads(false)}
          onSubmit={async (ids) => {
            setShowAddLeads(false);
            await callReOptimize(ids);
          }}
        />
      )}

      {showSendToPhone && (
        <SendToPhoneModal
          route={route}
          onClose={() => setShowSendToPhone(false)}
        />
      )}
    </div>
  );
}

// ─── ADD LEADS MODAL ──────────────────────────────────────────────────────────

function AddLeadsModal({
  existingLeadIds,
  onClose,
  onSubmit,
}: {
  existingLeadIds: string[];
  onClose: () => void;
  onSubmit: (ids: string[]) => void | Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [listFilter, setListFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "status" | "tag" | "source">("name");
  const [picked, setPicked] = useState<string[]>([]);

  // Same on-demand paging as the main builder — see fetchRoutableLeadPage. This modal has
  // no age inputs, so only state + search go to the server.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  const {
    data: addPages,
    isLoading,
    fetchNextPage: fetchMoreAdd,
    hasNextPage: hasMoreAdd,
    isFetchingNextPage: loadingMoreAdd,
  } = useInfiniteQuery({
    queryKey: ["leads-for-route-add", stateFilter, debouncedSearch],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchRoutableLeadPage(pageParam as number, {
        stateFilter,
        minAge: "",
        maxAge: "",
        search: debouncedSearch,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    staleTime: 60 * 1000,
  });

  const data = useMemo(
    () => (addPages?.pages ?? []).flatMap((p: any) => p.items),
    [addPages],
  );

  const regionStates = regionFilter ? REGIONS[regionFilter]?.states || [] : [];

  const allLeads: Lead[] = useMemo(() => {
    const items = data;
    if (!Array.isArray(items)) return [];
    const existing = new Set(existingLeadIds);
    return items.filter((l: Lead) => {
      if (existing.has(l.id)) return false;
      if (!l.address && !l.city && !l.zipCode) return false;
      if (l.leadStat && EXCLUDED_STATUSES.includes(l.leadStat)) return false;
      if (regionStates.length > 0 && l.state && !regionStates.includes(l.state.toUpperCase())) return false;
      return true;
    });
  }, [data, existingLeadIds, regionStates]);

  const listNames = useMemo(
    () => [...new Set(allLeads.map((l) => l.listName).filter(Boolean))].sort(),
    [allLeads],
  );
  const pipelineStatuses = useMemo(
    () => [...new Set(allLeads.map((l) => l.leadStat).filter(Boolean))].sort(),
    [allLeads],
  );
  const availableTags = useMemo(
    () => [...new Set(allLeads.flatMap((l) => (Array.isArray(l.tags) ? l.tags : [])).filter(Boolean))].sort(),
    [allLeads],
  );

  const filtered = useMemo(() => {
    let result = allLeads;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          l.name?.toLowerCase().includes(q) ||
          l.firstName?.toLowerCase().includes(q) ||
          l.lastName?.toLowerCase().includes(q) ||
          l.address?.toLowerCase().includes(q) ||
          l.city?.toLowerCase().includes(q) ||
          l.zipCode?.includes(q),
      );
    }
    if (pipelineFilter) result = result.filter((l) => l.leadStat === pipelineFilter);
    if (listFilter) result = result.filter((l) => l.listName === listFilter);
    if (tagFilter) result = result.filter((l) => Array.isArray(l.tags) && l.tags.includes(tagFilter));

    if (sortBy === "status") {
      result = [...result].sort((a, b) => (a.leadStat || "").localeCompare(b.leadStat || ""));
    } else if (sortBy === "tag") {
      result = [...result].sort((a, b) => {
        const at = Array.isArray(a.tags) && a.tags.length > 0 ? a.tags[0] : "￿";
        const bt = Array.isArray(b.tags) && b.tags.length > 0 ? b.tags[0] : "￿";
        return at.localeCompare(bt);
      });
    } else if (sortBy === "source") {
      result = [...result].sort((a, b) => (a.listName || "￿").localeCompare(b.listName || "￿"));
    } else {
      result = [...result].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return result;
  }, [allLeads, search, pipelineFilter, listFilter, tagFilter, sortBy]);

  const toggle = (id: string) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectAllVisible = () => {
    const visibleIds = filtered.map((l) => l.id);
    setPicked((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };
  const clearPicked = () => setPicked([]);

  const clearFilters = () => {
    setSearch("");
    setStateFilter("");
    setRegionFilter("");
    setPipelineFilter("");
    setListFilter("");
    setTagFilter("");
  };
  const hasActiveFilters =
    !!(search || stateFilter || regionFilter || pipelineFilter || listFilter || tagFilter);

  const selectClass =
    "h-9 text-xs rounded-md border border-gray-200 bg-white px-2 focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-gray-900">Add leads to route</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-4 py-3 border-b space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, address, city, ZIP…"
              className="pl-9"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <select className={selectClass} value={regionFilter} onChange={(e) => { setRegionFilter(e.target.value); setStateFilter(""); }}>
              <option value="">All regions</option>
              {Object.entries(REGIONS).map(([key, r]) => (
                <option key={key} value={key}>{r.label}</option>
              ))}
            </select>
            <select className={selectClass} value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              <option value="">All states</option>
              {(regionFilter ? regionStates : ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"]).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select className={selectClass} value={pipelineFilter} onChange={(e) => setPipelineFilter(e.target.value)}>
              <option value="">All statuses</option>
              {pipelineStatuses.map((p) => (
                <option key={p as string} value={p as string}>{p as string}</option>
              ))}
            </select>
            <select className={selectClass} value={listFilter} onChange={(e) => setListFilter(e.target.value)}>
              <option value="">All sources</option>
              {listNames.map((n) => (
                <option key={n as string} value={n as string}>{n as string}</option>
              ))}
            </select>
            <select className={selectClass} value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
              <option value="">All tags</option>
              {availableTags.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select className={selectClass} value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
              <option value="name">Sort: Name</option>
              <option value="status">Sort: Status</option>
              <option value="tag">Sort: Tag</option>
              <option value="source">Sort: Source</option>
            </select>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{picked.length} selected · {filtered.length} available</span>
            <div className="flex items-center gap-1.5">
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 px-2 text-[11px]">
                  Clear filters
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={selectAllVisible} className="h-6 px-2 text-[11px]">
                Select all
              </Button>
              {picked.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearPicked} className="h-6 px-2 text-[11px]">
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-12">
              No matching leads with addresses.
            </div>
          ) : (
            <ul className="divide-y">
              {filtered.map((lead) => {
                const isPicked = picked.includes(lead.id);
                return (
                  <li
                    key={lead.id}
                    onClick={() => toggle(lead.id)}
                    className={`px-4 py-2.5 cursor-pointer flex items-start gap-3 hover:bg-gray-50 ${
                      isPicked ? "bg-primary/5" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isPicked}
                      onChange={() => toggle(lead.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                        <span className="truncate">{lead.name || `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "Unnamed lead"}</span>
                        {lead.leadStat && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${getStatusColor(lead.leadStat)}`}>
                            {lead.leadStat}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {[lead.address, lead.city, lead.state, lead.zipCode].filter(Boolean).join(", ")}
                      </div>
                      {(lead.listName || (Array.isArray(lead.tags) && lead.tags.length > 0)) && (
                        <div className="text-[10px] text-gray-400 truncate mt-0.5">
                          {lead.listName ? `From: ${lead.listName}` : ""}
                          {lead.listName && Array.isArray(lead.tags) && lead.tags.length > 0 ? " · " : ""}
                          {Array.isArray(lead.tags) ? lead.tags.join(", ") : ""}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
              {hasMoreAdd && (
                <li className="p-3">
                  <button
                    type="button"
                    onClick={() => void fetchMoreAdd()}
                    disabled={loadingMoreAdd}
                    className="w-full rounded-md border border-gray-200 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-60"
                  >
                    {loadingMoreAdd ? "Loading…" : `Load ${ROUTE_LEAD_PAGE_SIZE} more`}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            disabled={picked.length === 0}
            onClick={() => onSubmit(picked)}
            className="gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            Add {picked.length || ""} & re-optimize
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── SEND TO PHONE MODAL ──────────────────────────────────────────────────────
// QR-encodes a Google Maps directions URL to the *next* stop only — scanning
// on a phone opens turn-by-turn nav directly in Google Maps (Android) or
// Safari → Maps app (iOS). No InsuraCentral mobile required.

function SendToPhoneModal({
  route,
  onClose,
}: {
  route: RoutePlan;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // Default the OS toggle to whatever the agent is currently using — most
  // people pair their iPad with an iPhone and their Windows laptop with
  // Android, so this is right ~80% of the time. They can flip if wrong.
  const defaultOs: "ios" | "android" = useMemo(() => {
    if (typeof navigator === "undefined") return "android";
    const ua = navigator.userAgent || "";
    const isIPadMac = /Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1;
    return /iPad|iPhone|iPod/.test(ua) || isIPadMac ? "ios" : "android";
  }, []);
  const [phoneOs, setPhoneOs] = useState<"ios" | "android">(defaultOs);

  // Find the next-up stop the agent is heading to. We do NOT encode a start
  // coordinate — the maps app uses the phone's live GPS as the origin, which
  // is exactly what the agent wants when scanning ("get me to this house
  // from where I'm standing right now").
  const nextStop = useMemo(() => {
    const stops = (route.stopDetails || []) as any[];
    const isDone = (s: any) => !!(s?.visited || s?.skipped);
    return stops.find(s => !isDone(s)) || null;
  }, [route]);

  // Build the directions URL based on the chosen phone OS:
  // - iOS: Apple Maps universal link (https://maps.apple.com) — iOS
  //   intercepts and opens the Maps app natively; saddr omitted = uses
  //   current location.
  // - Android: Google Maps universal link — Android intent opens the
  //   Google Maps app; origin omitted = "My Location".
  // We send the FULL ADDRESS STRING (not lat/lng) so each maps app uses
  // its own licensed/USPS-grade geocoder to find the exact house. Our
  // Nominatim/Census coords can be 1-3 houses off in rural areas;
  // Google/Apple data is more authoritative for door-knocking precision.
  // Coords are only a fallback when the lead has no address text.
  const directionsUrl = useMemo(() => {
    if (!nextStop) return null;
    // USPS-style address: `street, city, ST ZIP, USA`. A comma between
    // state and ZIP causes Google to reject rural lookups with
    // "can't find that address — add a city, state, or ZIP", so build
    // `state zip` as a single field.
    const street = ((nextStop as any).address || "").trim();
    const city = ((nextStop as any).city || "").trim();
    const state = ((nextStop as any).state || "").trim();
    const zip = ((nextStop as any).zipCode || "").trim();
    const stateZip = [state, zip].filter(Boolean).join(" ");
    const addr = [street, city, stateZip, "USA"].filter(Boolean).join(", ");
    const dest = addr
      ? encodeURIComponent(addr)
      : `${nextStop.latitude},${nextStop.longitude}`;
    if (phoneOs === "ios") {
      return `https://maps.apple.com/?daddr=${dest}&dirflg=d`;
    }
    // Legacy `maps.google.com/maps?daddr=` URL is more forgiving than
    // the newer `dir/?api=1&destination=` URL — falls back to fuzzy
    // search for unusual / typo'd / rural addresses instead of hard
    // failing with "can't find that address". Android intent layer
    // still upgrades this to the native Google Maps app.
    return `https://maps.google.com/maps?daddr=${dest}`;
  }, [nextStop, phoneOs]);

  const qrUrl = directionsUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=8&data=${encodeURIComponent(directionsUrl)}`
    : null;

  const handleCopy = async () => {
    if (!directionsUrl) return;
    try {
      await navigator.clipboard.writeText(directionsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be blocked in some contexts — silently no-op.
    }
  };

  const handleOpenHere = () => {
    if (directionsUrl) window.open(directionsUrl, "_blank");
  };

  const stopName = (nextStop as any)?.name || "the next stop";
  const stopAddress = nextStop
    ? [(nextStop as any).address, (nextStop as any).city, (nextStop as any).state]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">Send next stop to phone</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {nextStop ? `Going to ${stopName}` : "No remaining stops"}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-5 space-y-4">
          {!nextStop && (
            <p className="text-sm text-gray-500 text-center py-8">
              Every stop on this route is already visited or skipped.
            </p>
          )}

          {nextStop && (
            <>
              {/* Destination summary so the agent confirms which leg they're
                  about to navigate to before scanning. */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Destination</p>
                <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">{stopName}</p>
                {stopAddress && <p className="text-xs text-gray-500 truncate">{stopAddress}</p>}
              </div>

              {/* Phone OS picker — the QR regenerates so iPhone scans land in
                  Apple Maps, Android scans land in Google Maps. */}
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold text-center">Your phone</p>
                <div className="inline-flex rounded-md border border-gray-200 overflow-hidden w-full">
                  <button
                    type="button"
                    onClick={() => setPhoneOs("ios")}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                      phoneOs === "ios" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    Apple Maps
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhoneOs("android")}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${
                      phoneOs === "android" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    Google Maps
                  </button>
                </div>
              </div>

              {/* QR code — scan with the phone camera, native map app launches
                  with turn-by-turn directions queued up. */}
              <div className="flex flex-col items-center gap-2 py-2">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-2">
                  {qrUrl && (
                    <img
                      src={qrUrl}
                      alt={`QR code with directions to ${stopName}`}
                      className="w-[220px] h-[220px]"
                    />
                  )}
                </div>
                <p className="text-xs text-gray-600 text-center max-w-xs">
                  Scan with your phone camera. Directions start from your phone's current location.
                </p>
              </div>

              <Button
                variant="outline"
                onClick={handleOpenHere}
                className="w-full justify-center gap-2"
              >
                <Smartphone className="w-3.5 h-3.5" />
                Open here instead
              </Button>

              <Button
                variant="ghost"
                onClick={handleCopy}
                className="w-full justify-center gap-2 text-gray-600"
              >
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied!" : "Copy directions link"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function RoutesPage() {
  const [view, setView] = useState<"list" | "builder" | "detail">("list");
  const [activeRoute, setActiveRoute] = useState<RoutePlan | null>(null);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {view === "list" && (
        <RouteListView
          onCreateNew={() => setView("builder")}
          onOpenRoute={(route) => {
            setActiveRoute(route);
            setView("detail");
          }}
        />
      )}

      {view === "builder" && (
        <RouteBuilderView
          onBack={() => setView("list")}
          onRouteCreated={(route) => {
            setActiveRoute(route);
            setView("detail");
          }}
        />
      )}

      {view === "detail" && activeRoute && (
        <RouteDetailView
          route={activeRoute}
          onBack={() => {
            setActiveRoute(null);
            setView("list");
          }}
        />
      )}
    </div>
  );
}
